<template>
  <main>
    <div class="container">
      <al-modal
        :heading="$t('shippingAddress.deleteHeading')"
        :close-label="$t('button.cancel')"
        :cta-label="$t('button.delete')"
        name="delete-shipping-modal"
        @dismiss="cancelDelete"
        @cta="doDelete">
        <div v-if="addressToDelete" class="address-to-delete row margin-bottom-xl">
          <div class="col-full">
            <tux-address-label :address="addressToDelete" />
          </div>
        </div>
      </al-modal>
      <al-notify v-if="errorToken" error name="error-msg">
        {{ $t(errorToken) }}
      </al-notify>

      <div class="row">
        <div class="col">
          <h1 class="heading-xl align-center-space-between">
            {{ $t('shippingAddress.heading') }}
            <button
              v-analytics:click="$EVENTS.ADD_SHIPPING_BUTTON"
              :aria-label="$t('shippingAddress.addButton')"
              class="margin-bottom-none"
              icon
              data-test="add-shipping-btn"
              @click="addShipping()">
              <i class="icon-add" />
            </button>
          </h1>
        </div>
      </div>
      <div v-if="sortedShippingAddresses.length" class="row">
        <div class="col-full">
          <al-list card>
            <al-list-item
              v-for="shippingAddress in sortedShippingAddresses"
              :key="shippingAddress.id"
              :name="shippingAddress.id"
              data-test="shipping-list-item">
              <div>
                <tux-address-label :address="shippingAddress" class="p" />
              </div>
              <div slot="cta">
                <button
                  v-analytics:click="$EVENTS.DELETE_BUTTON"
                  v-toggle:al-modal="'delete-shipping-modal'"
                  :aria-label="getDeleteShippingAccessibilityLabel(shippingAddress)"
                  class="btn--secondary margin-bottom-none"
                  data-test="delete-shipping-btn"
                  @click="confirmDelete(shippingAddress)">
                  {{ $t('button.delete') }}
                </button>
              </div>
            </al-list-item>
          </al-list>
        </div>
      </div>
      <div v-else class="row row-vertical-align-center" data-test="address-empty-state">
        <div class="col-full align-center">
          <i class="h2 icon-shipping icon-size-lg" />
          <h3>
            {{ $t('shippingAddress.noAddresses') }}
          </h3>
          <p class="color-secondary">
            {{ $t('shippingAddress.addNewAddress') }}
          </p>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import { AlList, AlListItem, AlModal, AlNotify } from '@mc-alberta/alcapone'
import { TuxAddressLabel } from '@mc-alberta/tuxedo'
import { mapActions, mapGetters, mapState } from 'vuex'
import { LOADERS, ROUTES } from '@/constants'

export default {
  name: ROUTES.LIST_SHIPPING,
  components: { AlList, AlListItem, AlModal, AlNotify, TuxAddressLabel },
  data() {
    return {
      addressToDelete: null,
      errorToken: null
    }
  },
  computed: {
    ...mapState({
      serviceLayerError: (state) => state.serviceLayerError
    }),
    ...mapGetters(['sortedShippingAddresses', 'locale'])
  },
  created() {
    if (this.$i18nData.isCountryRestricted(this.locale)) {
      this.$router.replace({ name: ROUTES.PROFILE_HOME })
    }
  },
  beforeDestroy() {
    this.clearServiceLayerError()
  },
  methods: {
    ...mapActions([
      'clearServiceLayerError',
      'deleteShippingAddress',
      'startLoader',
      'stopLoader',
      'stopLoaderWithSuccess'
    ]),
    addShipping() {
      this.$router.push({ name: ROUTES.ADD_SHIPPING })
    },
    confirmDelete(address) {
      this.addressToDelete = address
    },
    cancelDelete() {
      this.$analytics.track(this.$EVENTS.MODAL_CANCEL_BUTTON)
      this.addressToDelete = null
    },
    async doDelete() {
      if (!this.addressToDelete) {
        // prevents a race condition issue where the user clicks delete while another delete is processing
        return
      }
      this.$analytics.track(this.$EVENTS.MODAL_DELETE_BUTTON)
      this.clearServiceLayerError()
      this.startLoader(LOADERS.UPDATE_PROFILE)

      await this.deleteShippingAddress(this.addressToDelete.id)

      this.errorToken = this.serviceLayerError ? 'error.default' : null
      this.addressToDelete = null

      if (this.serviceLayerError) {
        this.stopLoader(LOADERS.UPDATE_PROFILE)
      } else {
        this.stopLoaderWithSuccess(LOADERS.UPDATE_PROFILE)
      }
    },
    getDeleteShippingAccessibilityLabel(address) {
      const labelKeys = [
        'fullName',
        'line1',
        'line2',
        'line3',
        'cityTown',
        'subdivision',
        'postalCode'
      ]
      const itemLabel = labelKeys
        .reduce(
          (accumulator, key) => (address[key] ? accumulator + ` ${address[key]}` : accumulator),
          ''
        )
        .trim()

      return this.$t('accessibility.deleteItem', { label: itemLabel })
    }
  }
}
</script>

<style scoped>
.address-to-delete address {
  font-size: 16px;
}
</style>
